/**
 * Zone 2's submenu, as DATA (NAVIGATION.md; principle 9, the same rule `navigation.ts` follows
 * for zone 1).
 *
 * ---------------------------------------------------------------------------------------------
 * SEVEN ITEMS, IDENTICAL FOR A FESTIVAL AND A VENUE. NAVIGATION.md is explicit: "Undermenyen er
 * identisk for festival og venue inntil videre. Samme modell, samme punkter." So this is ONE
 * list, not a lookup keyed on `festivals.kind` — the day a venue needs its own set, the change
 * is a second array here and nothing in the components that render it.
 *
 * IT WAS SIX UNTIL 30 AUGUST. `PAGES.md` ends with Atle's own open question — should Economy be a
 * menu item of its own, between People and Documents — and his own recommendation that it should.
 * He answered his own question (sak 48): yes. It sits exactly where he put it, because the page is
 * a SUMMARY: cost lines are entered from Performances and from People, and a page that only rolls
 * them up belongs after the two pages that feed it.
 *
 * THE ADDRESS CARRIES THE ÅRGANG, THE MENU DOES NOT CHOOSE IT. "Årgang velges ikke i menyen"
 * (NAVIGATION.md, deliberate deviation 2): the year control lives at the top of the work area.
 * But every one of these pages IS about one year — a link to a programme is a link to one year's
 * programme — so the årgang is in the URL. Which one the menu points at is decided per row by
 * `EventNavItem`, not here: the year you are already in if you are inside that event, otherwise
 * its newest.
 *
 * THE PERMISSION IS THE ONE THAT OPENS THE PAGE (decision 0005). A menu asks the same question
 * the destination asks, and an item the reader may not have is ABSENT rather than greyed out
 * (NAVIGATION.md, "Tilgang styrer hva som vises"). Four of the seven deserve their reasoning
 * written down:
 *
 * - CALENDAR asks `task:list`, the same cell that opens the organisation's own `/calendar`. There
 *   is no `calendar:*` cell because there is no calendar resource: the grid draws tasks counted
 *   per day, and the year narrows WHAT is counted rather than WHO may count it. PAGES.md's
 *   timeline per stage will one day put the programme on it too; the day the page reads
 *   performances, this cell moves with the route's.
 * - DOCUMENTS asks `edition:read`. There is no `document:*` cell in the matrix — `RULES` has no
 *   rule for one, so `can()` denies every document action to everybody, including a superadmin.
 *   Naming one here would hide the item from the whole product rather than filter it. What the
 *   page is, until documents have cells of their own, is a part of the årgang you are reading.
 * - ECONOMY asks `economy:read`, which is the one cell here that exists BECAUSE of this menu entry.
 *   It borrowed `edition:update` for a day; Atle's answer to sak 53 was that the door belongs in
 *   Settings rather than in code — "slike ting skal helst ikke hardkodes" — so the action is real,
 *   it is in `ADJUSTABLE_ACTIONS`, and an organisation whose production manager owns the budget can
 *   widen it without making that person an admin. The default stays narrow: PAGES.md puts every
 *   cost line here, and vedtak 0008 sends artist fees here deliberately, away from the contact
 *   register where every `member` reads everything.
 * - SETTINGS asks `edition:update`, `yes` for superadmin and admin and `no` for the other three.
 *   That is PAGES.md's own sentence for the page ("Kun for admin og superadmin") expressed as a
 *   matrix row rather than as a role list in code, and it is the cell the page's own work would
 *   write with — every item PAGES.md lists under that heading configures one årgang. It is the
 *   one item of the six a `member` does not get, and NAVIGATION.md is explicit that they must not
 *   see it greyed: it is simply not there.
 *
 * Every one of the six is the cell its route guards on in `route-tree.ts`, checked against that
 * file rather than reasoned about twice. A menu that asks a different question from the page it
 * opens is a menu that either hides a page the reader may use or offers one they may not.
 * ---------------------------------------------------------------------------------------------
 */
import type { Action } from '@tvitvi/shared';

import type { TKey } from '@/i18n/types';

/**
 * The six addresses, as a union rather than as `LinkProps['to']`.
 *
 * They all take the same two params, so `<Link params={{ festivalId, editionId }}>` type-checks
 * against every member of this union. Typed as the whole `to` union it would not: the params of
 * `/legal/$slug` are in there too, and nothing satisfies all of them at once.
 */
export type EventNavPath =
  | '/festivals/$festivalId/editions/$editionId/calendar'
  | '/festivals/$festivalId/editions/$editionId/tasks'
  | '/festivals/$festivalId/editions/$editionId/performances'
  | '/festivals/$festivalId/editions/$editionId/people'
  | '/festivals/$festivalId/editions/$editionId/resources'
  | '/festivals/$festivalId/editions/$editionId/map'
  | '/festivals/$festivalId/editions/$editionId/summary'
  | '/festivals/$festivalId/editions/$editionId/documents'
  | '/festivals/$festivalId/editions/$editionId/settings';

/** Where the reader is, when the reader is inside an event at all. */
export interface EventLocation {
  festivalId: string;
  /** `null` on the event's own Overview, which has no year in its address. */
  editionId: string | null;
}

/**
 * The inverse of the addresses above, and it lives beside them on purpose: the shape of an event
 * URL is written down once, so the menu that builds them and the menu that recognises them
 * cannot drift.
 *
 * Read from the pathname rather than from `useParams`, because the sidebar is not inside any of
 * these routes — it is the shell around all of them — and because the one question it asks is a
 * PREFIX question ("are we anywhere inside this event"), which is what a path is.
 */
const EVENT_PATH = /^\/festivals\/([^/]+)(?:\/editions\/([^/]+))?/;

export function eventLocationOf(pathname: string): EventLocation | null {
  const match = EVENT_PATH.exec(pathname);
  const festivalId = match?.[1];
  if (festivalId === undefined) return null;
  return { festivalId, editionId: match?.[2] ?? null };
}

export interface EventNavItemDef {
  /** The i18n key suffix and the test id. */
  id: string;
  labelKey: TKey;
  to: EventNavPath;
  /** Cosmetic only, exactly as in `navigation.ts`: the route and the API refuse again. */
  permission: Action;
}

/**
 * «OVERSIKT», ØVERST I UNDERMENYEN (#20b) — og den ene raden som ikke har en årgang i adressen.
 *
 * ---------------------------------------------------------------------------------------------
 * HVORFOR DEN IKKE STÅR I LISTA UNDER. `EVENT_NAV_ITEMS` er åtte adresser som alle tar
 * `{festivalId, editionId}`, og det er det som gjør at `EventNavItem` kan bygge dem i én løkke.
 * Festivalens forside tar bare den første. En `editionId` sendt med på en rute som ikke har den er
 * ikke en skrivefeil TanStack Router godtar i stillhet, og et valgfritt felt i `EventNavItemDef`
 * ville gjort hver eneste rad til et spørsmål om hvilken av de to formene den har.
 *
 * HVORFOR DEN TRENGS NÅR NAVNET OVER PEKER PÅ DET SAMME. Raden med festivalnavnet ER en lenke til
 * `/festivals/:id`, og den har vært det hele tiden — men den er også utvideren, og det man ser når
 * den er foldet ut er åtte sider som alle handler om ett år. Etter #20a er forsiden en NIENDE side
 * med et innhold av sitt eget, og en side som bare kan nås ved å klikke på overskriften over
 * undermenyen er en side folk ikke finner. Atle ba om raden; dette er hvorfor den ikke er en
 * dobbeltføring.
 *
 * `festival:read` ER CELLA, som på ruta. Den er `yes` for fire roller og `grant` for en `external`,
 * så dette skjuler raden fra ingen som allerede ser festivalen i menyen.
 */
export const EVENT_NAV_OVERVIEW = {
  id: 'overview',
  labelKey: 'nav.event.overview',
  to: '/festivals/$festivalId',
  permission: 'festival:read',
} as const satisfies {
  id: string;
  labelKey: TKey;
  to: '/festivals/$festivalId';
  permission: Action;
};

/**
 * In NAVIGATION.md's order, which is not alphabetical and is not accidental: Calendar first
 * because it is what a production opens on a show day, Settings last because you go there
 * knowing what you want to change.
 *
 * No icons. Zone 1 has them because its entries are the only thing at their indent level; these
 * six sit indented under a name that already says which event they belong to, and DESIGN.md §5
 * forbids "ikoner ved siden av en etikett som allerede sier det samme" — a calendar glyph beside
 * the word Calendar is exactly that.
 */
export const EVENT_NAV_ITEMS: readonly EventNavItemDef[] = [
  {
    /*
     * KALENDERENS EGEN CELLE (rettelse B12, Atles avgjørelse 17. september).
     *
     * Raden spurte `task:list`. Lånet lakk begge veier: en organisasjon som skrudde AV oppgaver
     * for en rolle stengte kalenderen deres samtidig, og en som ville skjule kalenderen måtte ta
     * fra dem oppgavene. Standarden er tasks' egne celler, så ingen ser noe annet enn i går.
     */
    id: 'calendar',
    labelKey: 'nav.event.calendar',
    to: '/festivals/$festivalId/editions/$editionId/calendar',
    permission: 'event:list',
  },
  {
    id: 'tasks',
    labelKey: 'nav.event.tasks',
    to: '/festivals/$festivalId/editions/$editionId/tasks',
    permission: 'task:list',
  },
  {
    id: 'performances',
    labelKey: 'nav.event.performances',
    to: '/festivals/$festivalId/editions/$editionId/performances',
    permission: 'performance:list',
  },
  {
    id: 'people',
    labelKey: 'nav.event.people',
    to: '/festivals/$festivalId/editions/$editionId/people',
    // The people ON an event are references to Contacts and never new persons (PAGES.md), so the
    // question "may you see who is here" is the contact register's own. `limited` has it scoped
    // to their own department, which is a narrowed yes and still an entry.
    permission: 'contact:list',
  },
  /*
   * RESSURSER STO HER TIL 23. SEPTEMBER. Den er nå en fane i «Personer og ressurser» (over), og
   * adressen videresender dit (`route-tree.ts`).
   */
  /*
   * KART STO HER TIL 18. SEPTEMBER, rett under Ressurser.
   *
   * Atle, 17. september: «Menypunktet «Kart» fjernes; kartet legges som en fane under
   * Oppsummering inntil videre.» Kartet «viser ikke mye særlig» ennå, og en egen rad i menyen til
   * noe som er halvferdig er en rad man klikker på én gang. Adressen lever videre som en redirect
   * (`route-tree.ts`), så delte lenker og `?open=` fortsatt virker.
   *
   * Fanen har sin egen etikett (`summary.tabs.map`), så `nav.event.map` ble stående ubrukt og er
   * fjernet fra begge språkfilene. `map.title` lever videre — redirekt-ruta navngir seg med den.
   */
  {
    id: 'summary',
    labelKey: 'nav.event.summary',
    to: '/festivals/$festivalId/editions/$editionId/summary',
    // `performance:list` and not `economy:read`, since 3. september: three of the four tabs are
    // logistics rather than money, and hiding the whole entry behind the money cell would hide
    // them from everybody the money is hidden from. Økonomi stays its own tab behind its own
    // cell — the route header carries the argument.
    permission: 'performance:list',
  },
  {
    id: 'documents',
    labelKey: 'nav.event.documents',
    to: '/festivals/$festivalId/editions/$editionId/documents',
    permission: 'edition:read',
  },
  {
    id: 'settings',
    labelKey: 'nav.event.settings',
    to: '/festivals/$festivalId/editions/$editionId/settings',
    permission: 'edition:update',
  },
] as const;

/*
 * ---------------------------------------------------------------------------------------------
 * SPILLESTEDETS UNDERMENY — den andre lista (SPILLESTED-KONSERT, planen §6; sak 229).
 *
 * Et spillested har arrangementer, saler og datoforespørsler, ikke lineup og oppsummering.
 * Beholderen er den samme i basen; det er lista som skifter med typen.
 *
 * SAK 229: INGEN ÅRGANG. «Spillestedet organiseres rundt arrangementer uten årganger.» Alle sju
 * punktene har en adresse under `/venue/$festivalId` (Oversikt under `/festivals/$festivalId`, som
 * sender et spillested videre), og ingen av dem har et år i seg. Frivillige er tatt bort (229.5).
 * Innstillinger spør `festival:update`: siden redigerer spillestedet, ikke et år.
 * ---------------------------------------------------------------------------------------------
 */
/*
 * QA15-S B3: FEM PUNKTER. Arrangementer og Datoforespørsler er faner under hver sal i Saler (og under
 * «Alle saler»), ikke egne punkter. De gamle adressene sender videre.
 */
export type VenueNavTarget = 'overview' | 'rooms' | 'people' | 'resources' | 'settings';

export interface VenueNavItemDef {
  /** Test-id-en og nøkkelen. */
  id: VenueNavTarget;
  labelKey: TKey;
  /** Cosmetic only, as above: the route and the API refuse again. */
  permission: Action;
}

export const VENUE_NAV_ITEMS: readonly VenueNavItemDef[] = [
  { id: 'overview', labelKey: 'nav.venue.overview', permission: 'festival:read' },
  { id: 'rooms', labelKey: 'nav.venue.rooms', permission: 'venue:list' },
  { id: 'people', labelKey: 'nav.venue.people', permission: 'crew_member:list' },
  { id: 'resources', labelKey: 'nav.venue.resources', permission: 'resource:list' },
  { id: 'settings', labelKey: 'nav.venue.settings', permission: 'festival:update' },
] as const;

/** Listene per type: festivalen har sin, spillestedet sin. */
export const EVENT_NAV_BY_KIND = {
  festival: EVENT_NAV_ITEMS,
  venue: VENUE_NAV_ITEMS,
} as const;

const VENUE_PATH = /^\/venue\/([^/]+)/;

/** Spillestedet adressen står i, for `/venue/$festivalId/...` og for festivalens egne adresser. */
export function venueLocationOf(pathname: string): string | null {
  return VENUE_PATH.exec(pathname)?.[1] ?? eventLocationOf(pathname)?.festivalId ?? null;
}
